import { zeroQuotaIsCap, type ZeroQuotaCapInput } from "./credits-plan.ts";

export const CONVERSATION_LIMIT_GRACE_DAYS = 5;

export const CONVERSATION_LIMIT_TIME_ZONE = "America/Bogota";

const DAY_MS = 24 * 60 * 60 * 1000;

export const EXPLORA_PLAN_ID = "explora";

export function isExploraPlan(plan: string | null | undefined): boolean {
  return (plan ?? "").trim().toLowerCase() === EXPLORA_PLAN_ID;
}

export interface ConversationQuota {
  conversations_used: number | null;
  conversations_limit: number | null;
  conversations_extra?: number | null;
  overage_accepted_at?: string | null;
}

export function effectiveConversationLimit(quota: ConversationQuota): number | null {
  const limit = Number(quota.conversations_limit ?? 0);
  if (!(limit > 0)) return null;
  return limit + Number(quota.conversations_extra ?? 0);
}

export function isOverConversationLimit(quota: ConversationQuota | null | undefined): boolean {
  if (!quota || quota.overage_accepted_at) return false;
  const limit = effectiveConversationLimit(quota);
  if (limit === null) return false;
  return Number(quota.conversations_used ?? 0) > limit;
}

export interface ExtraConversationsBalance {
  granted: number;
  consumed: number;
  available: number;
}

export function extraConversationsBalance(
  quota: (ConversationQuota & ZeroQuotaCapInput) | null | undefined,
): ExtraConversationsBalance {
  const granted = Math.max(0, Math.floor(Number(quota?.conversations_extra ?? 0)) || 0);
  const limit = Number(quota?.conversations_limit ?? 0);
  const base = limit > 0 ? limit : zeroQuotaIsCap(quota) ? 0 : null;
  const used = Number(quota?.conversations_used ?? 0) || 0;
  const consumed = base === null ? 0 : Math.min(granted, Math.max(0, used - base));
  return { granted, consumed, available: granted - consumed };
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function conversationLimitCutoff(exceededAt: string | Date | null | undefined): Date | null {
  const start = toDate(exceededAt);
  return start ? new Date(start.getTime() + CONVERSATION_LIMIT_GRACE_DAYS * DAY_MS) : null;
}

export function isWithinConversationLimitGrace(
  exceededAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  const cutoff = conversationLimitCutoff(exceededAt);
  return cutoff !== null && now.getTime() < cutoff.getTime();
}

export function conversationLimitGraceDaysLeft(
  exceededAt: string | Date | null | undefined,
  now: Date = new Date(),
): number {
  const cutoff = conversationLimitCutoff(exceededAt);
  if (!cutoff) return 0;
  return Math.max(0, Math.ceil((cutoff.getTime() - now.getTime()) / DAY_MS));
}

export function formatConversationLimitCutoff(cutoff: Date): string {
  const date = new Intl.DateTimeFormat("es-CO", {
    timeZone: CONVERSATION_LIMIT_TIME_ZONE,
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(cutoff);
  const time = new Intl.DateTimeFormat("es-CO", {
    timeZone: CONVERSATION_LIMIT_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(cutoff);
  return `${date} a las ${time} (hora de Bogotá)`;
}

export function paidOverageConversations(reference: Record<string, unknown> | null | undefined): number {
  if (!reference || reference.overage_period_id) return 0;
  const conversations = Math.floor(Number(reference.overage_conversations ?? 0));
  return Number.isFinite(conversations) && conversations > 0 ? conversations : 0;
}

export function paidOverageCredit(quota: ConversationQuota | null | undefined, paidConversations: number): number {
  if (!quota || !(paidConversations > 0)) return 0;
  const limit = effectiveConversationLimit(quota);
  if (limit === null) return 0;
  const over = Number(quota.conversations_used ?? 0) - limit;
  return over > 0 ? Math.min(over, paidConversations) : 0;
}
